// New article checklist: create src/app/views/blog/articles/<slug>.html, import it and add an entry below, and add a /blog/<slug> line to prerender-routes.txt
import notionHabitTrackerProgressBarHtml from './articles/notion-habit-tracker-progress-bar.html';
import season0OpensOctober1Html from './articles/season-0-opens-october-1.html';
import howNotionHabitHeroesIsBuiltHtml from './articles/how-notion-habit-heroes-is-built.html';
import whatsNewMonthly202610Html from './articles/whats-new-monthly-2026-10.html';
import whatsNewMonthly202609Html from './articles/whats-new-monthly-2026-09.html';
import whatsNewMonthly202608Html from './articles/whats-new-monthly-2026-08.html';
import whatsNew20261101Html from './articles/whats-new-2026-11-01.html';
import whatsNew20261025Html from './articles/whats-new-2026-10-25.html';
import whatsNew20261018Html from './articles/whats-new-2026-10-18.html';
import whatsNew20261011Html from './articles/whats-new-2026-10-11.html';
import whatsNew20261004Html from './articles/whats-new-2026-10-04.html';
import whatsNew20260920Html from './articles/whats-new-2026-09-20.html';
import whatsNew20260914Html from './articles/whats-new-2026-09-14.html';
import whatsNew20260906Html from './articles/whats-new-2026-09-06.html';
import whatsNew20260830Html from './articles/whats-new-2026-08-30.html';
import whatsNew20260823Html from './articles/whats-new-2026-08-23.html';
import whatsNew20260816Html from './articles/whats-new-2026-08-16.html';
import whatsNew20260809Html from './articles/whats-new-2026-08-09.html';
import whatsNew20260802Html from './articles/whats-new-2026-08-02.html';
import whatsNew20260726Html from './articles/whats-new-2026-07-26.html';
import notionHabitCompetitionsHtml from './articles/notion-habit-competitions.html';
import bestNotionHabitTrackerTemplatesHtml from './articles/best-notion-habit-tracker-templates.html';
import notionHabitTrackerMistakesHtml from './articles/notion-habit-tracker-mistakes.html';
import notionHabitTrackerGoogleCalendarHtml from './articles/notion-habit-tracker-google-calendar.html';
import gamifiedNotionHabitTrackerHtml from './articles/gamified-notion-habit-tracker.html';
import storyBehindNotionHabitHeroesHtml from './articles/story-behind-notion-habit-heroes.html';
import bestHabitHeroAppsHtml from './articles/best-habit-hero-apps.html';
import habitHeroTemplateVsNotionHabitHeroesHtml from './articles/habit-hero-template-vs-notion-habit-heroes.html';
import socialHabitTrackingHtml from './articles/social-habit-tracking.html';
import gamifyYourHabitsHtml from './articles/gamify-your-habits.html';
import notionHabitTrackerHtml from './articles/notion-habit-tracker.html';
import teamHabitChallengeNotionHtml from './articles/team-habit-challenge-notion.html';
import clientAccountabilityNotionCoachesHtml from './articles/client-accountability-notion-coaches.html';
import notionHabitHeroesIsNowFreeHtml from './articles/notion-habit-heroes-is-now-free.html';

export type BlogCategory = 'guide' | 'essay' | 'engineering' | 'changelog';

export const CATEGORY_LABELS: Record<BlogCategory, string> = {
  guide: 'Guides',
  essay: 'Essays',
  engineering: 'Engineering',
  changelog: 'Changelog',
};

export interface BlogArticle {
  slug: string;
  category: BlogCategory;      // blog-index grouping: filter chips + card pill; labels in CATEGORY_LABELS
  title: string;
  seoTitle: string;
  description: string;
  datePublished: string;        // ISO date
  dateModified?: string;
  image: string;                // site-relative asset path
  thumbnail?: string;           // lighter list/index image; falls back to image
  imageAlt: string;
  excerpt: string;
  html: string;                 // article body HTML — author-controlled repo content
  draft?: boolean;              // hidden from blog index; not in prerender-routes.txt → never prerendered/sitemapped; preview via ng serve or a deploy-preview
  featured?: boolean;           // at most one article should carry this; controls the blog-index hero slot
  faq?: { question: string; answer: string }[];  // FAQ entries for FAQPage JSON-LD
  hideHero?: boolean;           // image is used for OG/social meta only, not rendered as the in-page hero
}

export const ARTICLES: BlogArticle[] = [
  {
    slug: 'notion-habit-tracker-progress-bar',
    category: 'guide',
    title: 'How to Build a Notion Habit Tracker With a Progress Bar',
    seoTitle: 'Notion Habit Tracker with Progress Bar: Step-by-Step',
    description:
      'Add a progress bar to your Notion habit tracker: a formula that counts ticked habits, shown as a bar, plus a weekly bar and live streak bars.',
    datePublished: '2026-10-24',
    image: 'assets/images/blog/og-blog-notion-habit-tracker-progress-bar.png',
    thumbnail: 'assets/images/blog/og-blog-notion-habit-tracker-progress-bar_800.webp',
    imageAlt:
      'Notion Habit Heroes blog card: How to Build a Notion Habit Tracker With a Progress Bar, beside a white card listing five weekdays, each with a progress bar and a count of habits done.',
    excerpt:
      'Notion can show any number as a bar. Here is the formula that turns daily habit checkboxes into a progress bar, plus a weekly version. I also cover where it breaks down and how to get live bars without formulas.',
    html: notionHabitTrackerProgressBarHtml,
    faq: [
      {
        question: 'Can Notion show a progress bar for habits?',
        answer: 'Yes. A number property or a formula that returns a number can be shown as a bar or a ring instead of a plain number. For a daily habit database, a formula that adds up the ticked checkboxes, shown as a bar with Divide by set to your number of habits, gives every day its own progress bar.',
      },
      {
        question: 'How do I make a weekly habit progress bar in Notion?',
        answer: 'A formula only sees its own row, so you need a second database with one page per week. Link each day page to its week with a relation. Then sum the daily count with a rollup on the week page. A formula that divides that sum by the maximum possible for the week can then be shown as a bar.',
      },
      {
        question: 'Can a Notion progress bar track habit streaks?',
        answer: "Not on its own. A formula cannot look at yesterday's row, so streaks in plain Notion need fragile chains of relations or rollups. Notion Habit Heroes reads your checkboxes through the Notion API, counts streaks from the actual history, then shows each streak as a bar toward your longest one.",
      },
    ],
  },
  {
    slug: 'season-0-opens-october-1',
    category: 'changelog',
    title: 'Season 0 opens October 1: the founding class names Season 1',
    seoTitle: 'Season 0 Opens October 1: Earn the Founding Hero Badge',
    description:
      'Season 0: Founding Heroes runs October 1 to 31, 2026. Finish it and you earn the Founding Hero badge, awarded once. Every finisher is named in the hall of fame. The top scorer names Season 1.',
    datePublished: '2026-09-16',
    dateModified: '2026-11-01',
    image: 'assets/images/blog/og-season-0-opens-october-1-tiles.png',
    thumbnail: 'assets/images/blog/og-season-0-opens-october-1-tiles_800.webp',
    imageAlt:
      'Notion Habit Heroes blog card: Season 0 opens October 1, with four tiles reading 31 days, October 1 to 31; Free for every account; 1 badge for every finisher; #1 names Season 1',
    excerpt:
      'A checkbox you tick in private is easy to skip. A shared board is not. On October 1 the first public season starts: one leaderboard, free, open to every account. Finishers keep a badge. Whoever tops it names Season 1.',
    html: season0OpensOctober1Html,
    hideHero: true,
  },
  {
    slug: 'how-notion-habit-heroes-is-built',
    category: 'engineering',
    title: 'How Notion Habit Heroes Is Built',
    seoTitle: 'How Notion Habit Heroes Is Built: Stack Behind the App',
    description:
      'A tour of the tech behind Notion Habit Heroes: live sync on widget open with a nightly safety net, self-healing jobs, a framework-free widget, a serverless site, plus cookieless analytics.',
    datePublished: '2026-08-11',
    image: 'assets/images/blog/og-blog-how-notion-habit-heroes-is-built.png',
    thumbnail: 'assets/images/blog/og-blog-how-notion-habit-heroes-is-built_800.webp',
    imageAlt:
      'Notion Habit Heroes blog card: How Notion Habit Heroes Is Built, above four tiles reading nightly sync in your timezone, self-healing jobs, a widget with zero frameworks, analytics with zero cookies.',
    excerpt:
      'What actually happens after you connect your workspace? A tour of the machinery: live sync on widget open with a nightly safety net in your timezone, self-healing jobs, a widget with zero frameworks, analytics with zero cookies.',
    html: howNotionHabitHeroesIsBuiltHtml,
  },
  {
    slug: 'team-habit-challenge-notion',
    category: 'guide',
    title: 'How to Run a Team Habit Challenge in Notion (Step by Step)',
    seoTitle: 'Team Habit Challenge in Notion: Step-by-Step Guide',
    description:
      'How to run a team habit challenge in Notion: picking shared habits, setting a window, a weekly recap, plus simple scoring that does not fall apart by week two.',
    datePublished: '2026-08-02',
    image: 'assets/images/blog/og-blog-team-habit-challenge-notion.png',
    thumbnail: 'assets/images/blog/og-blog-team-habit-challenge-notion_800.webp',
    imageAlt:
      'Notion Habit Heroes blog card: How to Run a Team Habit Challenge in Notion (Step by Step), above a white leaderboard card ranking three teams with progress bars and points.',
    excerpt:
      'My first team habit challenge died in nine days. Here is the structure that fixed it: 3 to 5 shared habits, a real end date, a Monday recap, plus scoring anyone can hold in their head.',
    html: teamHabitChallengeNotionHtml,
  },
  {
    slug: 'client-accountability-notion-coaches',
    category: 'guide',
    title: 'Client Accountability for Coaches Who Use Notion',
    seoTitle: 'Client Accountability in Notion: A Guide for Coaches',
    description:
      'Why screenshot check-ins fail coaches, what a good accountability loop looks like, how to set one up in Notion, plus where the manual approach hits a wall.',
    datePublished: '2026-08-02',
    image: 'assets/images/blog/og-blog-client-accountability-notion-coaches.png',
    thumbnail: 'assets/images/blog/og-blog-client-accountability-notion-coaches_800.webp',
    imageAlt:
      'Notion Habit Heroes blog card: Client Accountability for Coaches Who Use Notion, beside a dashboard card listing three clients with streak-day squares and a status dot each.',
    excerpt:
      'Clients screenshot the good weeks and go quiet on the bad ones. Here is what a real accountability loop looks like, how to build one in Notion today, plus where doing it by hand stops scaling.',
    html: clientAccountabilityNotionCoachesHtml,
  },
  {
    slug: 'notion-habit-heroes-is-now-free',
    category: 'essay',
    title: 'Notion Habit Heroes Is Now Completely Free',
    seoTitle: 'Notion Habit Heroes Is Now Completely Free',
    description:
      "Notion Habit Heroes is now free for individuals: competitions, duels, insights, the whole tracker, no trial, no card, no tier. Here's the honest reason why.",
    datePublished: '2026-08-02',
    image: 'assets/images/blog/og-blog-notion-habit-heroes-is-now-free.png',
    thumbnail: 'assets/images/blog/og-blog-notion-habit-heroes-is-now-free_800.webp',
    imageAlt:
      'Notion Habit Heroes blog card: Notion Habit Heroes Is Now Completely Free, showing the Pro plan struck through and replaced by a FREE badge, with chips for Competitions, Duels and Insights.',
    excerpt:
      "I just removed the individual Pro tier entirely. Competitions, duels, insights, streaks, the whole tracker: free for everyone now, no trial, no card, no tier. Here's why, plus what happens to the people who paid me for it.",
    html: notionHabitHeroesIsNowFreeHtml,
  },
  {
    slug: 'whats-new-2026-11-01',
    title: "What's new in Notion Habit Heroes: week of October 25, 2026",
    seoTitle: "What's New in Notion Habit Heroes: October 25, 2026",
    description:
      "Season 0's finish line: a podium page, the Founding Hero medal, an on-time close. Sign in with Notion, a welcome mail, a dozen wording fixes. 75 changes.",
    datePublished: '2026-11-01',
    image: 'assets/images/blog/og-whats-new-2026-11-01-tiles.png',
    thumbnail: 'assets/images/blog/og-whats-new-2026-11-01-tiles_800.webp',
    imageAlt:
      'Weekly ship log for October 25 to 31, 2026: 75 changes merged across 7 days, 8 production deploys, 30 backlog items cleared',
    excerpt:
      "Season 0 reached its finish line this week, the busiest of October: 75 changes merged, twenty of them on Wednesday and nineteen on Friday. The podium of a finished season got a home on the site and in the widget. The Founding Hero badge became a medal. A time-zone slip that would have closed the season at noon on its last day was caught the day before. You can sign in with Notion now. New accounts get a welcome mail with the setup steps.",
    html: whatsNew20261101Html,
    category: 'changelog',
    draft: false,
    hideHero: true,
  },
  {
    slug: 'whats-new-2026-10-25',
    title: "What's new in Notion Habit Heroes: October 18 to 24, 2026",
    seoTitle: "What's New in Notion Habit Heroes: October 18 to 24, 2026",
    description:
      'Edit Habits rebuilt from a phone, an honest completion rate for weekly habits, the free-template roundup refreshed to twelve, three new guides.',
    datePublished: '2026-10-25',
    image: 'assets/images/blog/og-whats-new-2026-10-25-tiles.png',
    thumbnail: 'assets/images/blog/og-whats-new-2026-10-25-tiles_800.webp',
    imageAlt:
      'Weekly ship log for October 18 to 24, 2026: 44 changes merged across 7 days, 3 production deploys, 23 backlog items cleared',
    excerpt:
      "A quiet middle and two busy weekend days: 44 changes merged, thirty-four of them on Sunday and Saturday. The widget's Edit Habits screen was rebuilt around fifteen findings from a phone. Weekly habits now get a completion rate that means something. The roundup of free templates grew to twelve, every price checked live. Three new pages explain the widget, progress bars in Notion and tracking habits with friends.",
    html: whatsNew20261025Html,
    category: 'changelog',
    draft: false,
    hideHero: true,
  },
  {
    slug: 'whats-new-monthly-2026-10',
    title: "What's new in Notion Habit Heroes: the October roundup",
    seoTitle: 'Notion Habit Heroes Monthly Roundup: October 2026',
    description:
      'Four weekly ship logs for October 2026: Season 0 opened, the knight was redrawn, a first connect stopped being empty, private habits arrived. 159 changes.',
    datePublished: '2026-10-31',
    image: 'assets/images/blog/og-whats-new-monthly-2026-10-tiles.png',
    thumbnail: 'assets/images/blog/og-whats-new-monthly-2026-10-tiles_800.webp',
    imageAlt:
      'Monthly ship log roundup for September 28 to October 24, 2026: 159 changes merged across 27 days, 18 production deploys, 92 backlog items cleared',
    excerpt:
      'Four weekly editions cover October, the month of Season 0: 159 changes in 27 days, 18 deploys, 92 backlog items cleared. Season 0 opened on October 1 with a trailer and an announcement mail. The knight on the logo was redrawn and reached every mail. Connecting Notion stopped opening on an empty week. Private habits, honest completion rates and a widget that knows when it is disconnected followed.',
    html: whatsNewMonthly202610Html,
    category: 'changelog',
    draft: false,
    hideHero: true,
  },
  {
    slug: 'whats-new-2026-10-18',
    title: "What's new in Notion Habit Heroes: week of October 18, 2026",
    seoTitle: "What's New in Notion Habit Heroes: October 18, 2026",
    description:
      'Private habits hidden from peers, an example week in the template, a widget that knows when it is not connected, and mails that say what broke.',
    datePublished: '2026-10-18',
    image: 'assets/images/blog/og-whats-new-2026-10-18-tiles.png',
    thumbnail: 'assets/images/blog/og-whats-new-2026-10-18-tiles_800.webp',
    imageAlt:
      'Weekly ship log for October 18, 2026: 47 changes merged across 7 days, 7 production deploys, 31 backlog items cleared',
    excerpt:
      'A steady start and a heavy weekend: 47 changes merged, thirty of them on Saturday and Sunday. Habits can now be marked private, so a habit you would rather keep to yourself never shows up for anyone else. The public template carries an example week, so a fresh copy is no longer empty. The widget tells you when the account behind it is disconnected or gone. Midnight tasks now survive a deploy that lands at the wrong minute.',
    html: whatsNew20261018Html,
    category: 'changelog',
    draft: false,
    hideHero: true,
  },
  {
    slug: 'whats-new-2026-10-11',
    title: "What's new in Notion Habit Heroes: week of October 11, 2026",
    seoTitle: "What's New in Notion Habit Heroes: October 11, 2026",
    description:
      'Leaderboards hide zero-point rows, competitions start from the widget, a first connect seeds your habits. Tracking without Notion: built, not on yet.',
    datePublished: '2026-10-11',
    image: 'assets/images/blog/og-whats-new-2026-10-11-tiles.png',
    thumbnail: 'assets/images/blog/og-whats-new-2026-10-11-tiles_800.webp',
    imageAlt:
      'Weekly ship log for October 11, 2026: 37 changes merged across 7 days, 4 production deploys, 26 backlog items cleared',
    excerpt:
      'The first full week of Season 0 merged 37 changes. Competition leaderboards no longer list people at zero points. A first Notion connect starts with the habits your journal already has. Competitions can be created and left from inside the widget. Seven changes toward tracking without Notion merged, all behind a switch that stays off until a full rehearsal has passed. The redrawn knight reached the mails. Signup gained a newsletter checkbox. The weekend went to a usability sweep of the site.',
    html: whatsNew20261011Html,
    category: 'changelog',
    draft: false,
    hideHero: true,
  },
  {
    slug: 'whats-new-2026-10-04',
    title: "What's new in Notion Habit Heroes: week of October 4, 2026",
    seoTitle: "What's New in Notion Habit Heroes: October 4, 2026",
    description:
      'Season 0 opened on October 1. The week around it: the launch trailer, a redrawn knight logo, phone fixes for the widget, one-click joining on connect.',
    datePublished: '2026-10-04',
    image: 'assets/images/blog/og-whats-new-2026-10-04-tiles.png',
    thumbnail: 'assets/images/blog/og-whats-new-2026-10-04-tiles_800.webp',
    imageAlt:
      'Weekly ship log for October 4, 2026: 31 changes merged across 7 days, 4 production deploys, 12 backlog items cleared',
    excerpt:
      'Season 0: Founding Heroes opened on October 1. The week around it merged 31 changes in two bursts, before and after the start. The launch trailer went out, the knight on the logo was redrawn and centred, the widget got its phone layout fixed, the season showed as running a day early and the site now corrects for it. Since Sunday evening a first Notion connect enrols you in the season automatically.',
    html: whatsNew20261004Html,
    category: 'changelog',
    draft: false,
    hideHero: true,
  },
  {
    slug: 'whats-new-monthly-2026-09',
    title: "What's new in Notion Habit Heroes: the September roundup",
    seoTitle: 'Notion Habit Heroes Monthly Roundup: September 2026',
    description:
      'Three weekly ship logs for September 2026: the outage ended, Season 0 took shape, scoring got one rule, teams and coaches got their spaces. 176 changes.',
    datePublished: '2026-09-30',
    image: 'assets/images/blog/og-whats-new-monthly-2026-09-tiles.png',
    thumbnail: 'assets/images/blog/og-whats-new-monthly-2026-09-tiles_800.webp',
    imageAlt:
      'Monthly ship log roundup for August 31 to September 20, 2026: 176 changes merged across 21 days, 15 production deploys, 92 backlog items cleared',
    excerpt:
      'Three weekly editions cover September. The line to Notion came back after a two-week outage caused by one request header of ours, then the backlog behind it moved: 176 changes in 21 days, 15 deploys, 92 backlog items cleared. Season 0 got its rules, countdown and widget card. Scoring was rebuilt around one rule. Teams and coaches got their spaces.',
    html: whatsNewMonthly202609Html,
    category: 'changelog',
    draft: false,
    hideHero: true,
  },
  {
    slug: 'whats-new-2026-09-20',
    title: "What's new in Notion Habit Heroes: week of September 20, 2026",
    seoTitle: "What's New in Notion Habit Heroes: September 20, 2026",
    description:
      'Six days in Notion Habit Heroes: habits in your order, Season 0 in the widget, scoring from the day before you join, a newsletter fix, a new hero video.',
    datePublished: '2026-09-20',
    image: 'assets/images/blog/og-whats-new-2026-09-20-tiles.png',
    thumbnail: 'assets/images/blog/og-whats-new-2026-09-20-tiles_800.webp',
    imageAlt:
      'Weekly ship log for September 20, 2026: 36 changes merged across 6 days, 5 production deploys, 12 backlog items cleared',
    excerpt:
      "A short week of six days with two bursts: 21 changes on Tuesday, 11 on Sunday. Your habits keep the order you give them, the widget shows Season 0 to anyone not in a competition yet, competition scoring got one rule: you score from the day before you join. A newsletter bug that quietly skipped some subscribers is fixed. The Season 0 post is live, the landing page has a new hero video.",
    html: whatsNew20260920Html,
    category: 'changelog',
    draft: false,
    hideHero: true,
  },
  {
    slug: 'whats-new-2026-09-14',
    title: "What's new in Notion Habit Heroes: September 7 to 14, 2026",
    seoTitle: "What's New in Notion Habit Heroes: September 14, 2026",
    description:
      'Eight days in Notion Habit Heroes: shared team habits, a template-aware connect, scores that revisit yesterday, a Season 0 countdown, a home-screen widget.',
    datePublished: '2026-09-14',
    image: 'assets/images/blog/og-whats-new-2026-09-14-tiles.png',
    thumbnail: 'assets/images/blog/og-whats-new-2026-09-14-tiles_800.webp',
    imageAlt:
      'Ship log for September 14, 2026: 63 changes merged across 8 days, 3 production deploys, 35 backlog items cleared',
    excerpt:
      "This edition runs eight days because Sunday's edition did not go out. 63 changes, 41 of them on the weekend. The start page shows where you stand after connecting, teams share one habit list, scheduled habits re-settle yesterday's edits, connecting from the template goes straight to the right database, Season 0 has its countdown with one join link. The widget can live on your phone's home screen.",
    html: whatsNew20260914Html,
    category: 'changelog',
    draft: false,
    hideHero: true,
  },
  {
    slug: 'whats-new-2026-09-06',
    title: "What's new in Notion Habit Heroes: week of September 6, 2026",
    seoTitle: "What's New in Notion Habit Heroes: September 6, 2026",
    description:
      'This week in Notion Habit Heroes: the outage ends, two competitions at once, Season 0 rules for October, one-click database repair, team and coach spaces.',
    datePublished: '2026-09-06',
    image: 'assets/images/blog/og-whats-new-2026-09-06-tiles.png',
    thumbnail: 'assets/images/blog/og-whats-new-2026-09-06-tiles_800.webp',
    imageAlt:
      'Weekly ship log for September 6, 2026: 77 changes merged across 7 days, 7 production deploys, 45 backlog items cleared',
    excerpt:
      'The line to Notion came back on Tuesday. Then the backlog moved: 77 changes in seven days, 60 of them on the weekend. You can hold a regular competition and the featured season at once, Season 0 has its rules page for October, a journal database that fails the schema check repairs itself with one click, badges show on your profile. Team spaces and coach spaces are rolling out.',
    html: whatsNew20260906Html,
    category: 'changelog',
    draft: false,
    hideHero: true,
  },
  {
    slug: 'whats-new-monthly-2026-08',
    title: "What's new in Notion Habit Heroes: the first monthly roundup",
    seoTitle: 'Notion Habit Heroes Monthly Roundup: August 2026',
    description:
      'Six weekly ship logs building Notion Habit Heroes: the tracker went free, an API migration closed, an outage traced to our code. 386 changes in 39 days.',
    datePublished: '2026-08-16',
    dateModified: '2026-09-05',
    image: 'assets/images/blog/og-whats-new-monthly-2026-08-tiles.png',
    thumbnail: 'assets/images/blog/og-whats-new-monthly-2026-08-tiles_800.webp',
    imageAlt:
      'Monthly ship log roundup for July 23 to August 30, 2026: 386 changes merged across 39 days, 39 production deploys, 184 backlog items cleared',
    excerpt:
      'Six weekly editions make more than a month. The tracker went free. The Notion API migration ran start to finish. Season 0: Founding Heroes opened its gates. A Notion outage turned out to be caused by our own code. The numbers: 386 changes, 39 deploys, 184 backlog items cleared.',
    html: whatsNewMonthly202608Html,
    category: 'changelog',
    draft: false,
    hideHero: true,
  },
  {
    slug: 'whats-new-2026-08-30',
    title: "What's new in Notion Habit Heroes: week of August 30, 2026",
    seoTitle: "What's New in Notion Habit Heroes: August 30, 2026",
    description:
      'This week in Notion Habit Heroes: week two of the Notion outage, the correction that followed, loud failures, all-or-nothing connects, a circuit breaker.',
    datePublished: '2026-08-30',
    dateModified: '2026-09-05',
    image: 'assets/images/blog/og-whats-new-2026-08-30-tiles.png',
    thumbnail: 'assets/images/blog/og-whats-new-2026-08-30-tiles_800.webp',
    imageAlt:
      'Weekly ship log for August 30, 2026: 16 changes merged across 7 days, 1 production deploy, 7 backlog items cleared',
    excerpt:
      'The week the app learned to fail honestly. The Notion outage entered week two; a rebuilt integration got rejected too, which looked like proof against Notion. It was not: on September 1 the cause turned up in our own code. Meanwhile: overnight tasks fail loudly now, connects roll back cleanly, one shared outage no longer reads as many personal ones.',
    html: whatsNew20260830Html,
    category: 'changelog',
    draft: false,
    hideHero: true,
  },
  {
    slug: 'whats-new-2026-08-23',
    title: "What's new in Notion Habit Heroes: week of August 23, 2026",
    seoTitle: "What's New in Notion Habit Heroes: August 23, 2026",
    description:
      'This week in Notion Habit Heroes: a Notion outage handled in the open, sign-in forgives email capitalization, competitions as a list, self-finishing ones.',
    datePublished: '2026-08-23',
    image: 'assets/images/blog/og-whats-new-2026-08-23-tiles.png',
    thumbnail: 'assets/images/blog/og-whats-new-2026-08-23-tiles_800.webp',
    imageAlt:
      'Weekly ship log for August 23, 2026: 27 changes merged across 7 days, 1 production deploy, 15 backlog items cleared',
    excerpt:
      'The week the sync line went dead. A Notion API outage blocked our servers, so the site said so plainly while the case sat with Notion support. The cause turned out to be ours; the September 1 correction is inside. Shipping continued: sign-in forgives capitalization, your competitions became a list, competitions finish on their own now.',
    html: whatsNew20260823Html,
    category: 'changelog',
    draft: false,
    hideHero: true,
  },
  {
    slug: 'whats-new-2026-08-16',
    title: "What's new in Notion Habit Heroes: week of August 16, 2026",
    seoTitle: "What's New in Notion Habit Heroes: August 16, 2026",
    description:
      'This week in Notion Habit Heroes: Season 0 Founding Heroes opens on the homepage, share links for competitions, removed habits keep history, a new logo.',
    datePublished: '2026-08-16',
    dateModified: '2026-09-05',
    image: 'assets/images/blog/og-whats-new-2026-08-16-tiles.png',
    thumbnail: 'assets/images/blog/og-whats-new-2026-08-16-tiles_800.webp',
    imageAlt:
      'Weekly ship log for August 16, 2026: 132 changes merged across 7 days, 20 production deploys, 68 backlog items cleared',
    excerpt:
      'The first sitewide season is open for signups: Season 0 Founding Heroes, right on the homepage. Competitions learned share links with real previews. Removed habits keep their history now. The site wears a new knight mark.',
    html: whatsNew20260816Html,
    category: 'changelog',
    draft: false,
    hideHero: true,
  },
  {
    slug: 'whats-new-2026-08-09',
    title: "What's new in Notion Habit Heroes: week of August 9, 2026",
    seoTitle: "What's New in Notion Habit Heroes: August 9, 2026",
    description:
      'This week in Notion Habit Heroes: the tracker is free for everyone, a year-long habit heatmap, up to three duels at once, plus freeze-day corrections.',
    datePublished: '2026-08-09',
    image: 'assets/images/blog/og-whats-new-2026-08-09-tiles.png',
    thumbnail: 'assets/images/blog/og-whats-new-2026-08-09-tiles_800.webp',
    imageAlt:
      'Weekly ship log for August 9, 2026: 92 changes merged across 7 days, 12 production deploys, 35 backlog items cleared',
    excerpt:
      'The individual Pro tier is gone: the whole tracker is free now. Insights gained a year-long habit heatmap. Duels can run three at a time. Freeze days got a run of corrections. The widget finally fits a phone.',
    html: whatsNew20260809Html,
    category: 'changelog',
    draft: false,
    hideHero: true,
  },
  {
    slug: 'whats-new-2026-08-02',
    title: "What's new in Notion Habit Heroes: week of August 2, 2026",
    seoTitle: "What's New in Notion Habit Heroes: August 2, 2026",
    description:
      'This week in Notion Habit Heroes: per-habit duels, a live demo on the template page, free freeze days, one-click unsubscribe, plus group accounts.',
    datePublished: '2026-08-02',
    image: 'assets/images/blog/og-whats-new-2026-08-02-tiles.png',
    thumbnail: 'assets/images/blog/og-whats-new-2026-08-02-tiles_800.webp',
    imageAlt:
      'Weekly ship log for August 2, 2026: 89 changes merged across 7 days, 4 production deploys, 35 backlog items cleared',
    excerpt:
      'The tide came in twice this week. Duels learned to fight over a single habit. The template page got a live demo. Freeze days went free for everyone. Under the surface, the app moved to Notion\'s new API model.',
    html: whatsNew20260802Html,
    category: 'changelog',
    draft: false,
    hideHero: true,
  },
  {
    slug: 'whats-new-2026-07-26',
    title: "What's new in Notion Habit Heroes: week of July 26, 2026",
    seoTitle: "What's New in Notion Habit Heroes: July 26, 2026",
    description:
      'This week in Notion Habit Heroes: past-competition history, top-ten leaderboards, live duel updates, same-day freeze undo, and a stack of fixes.',
    datePublished: '2026-07-26',
    image: 'assets/images/blog/og-whats-new-2026-07-26-tiles.png',
    thumbnail: 'assets/images/blog/og-whats-new-2026-07-26-tiles_800.webp',
    imageAlt:
      'Weekly ship log for July 26, 2026: 30 changes merged across 4 days, 1 production deploy, 24 backlog items cleared',
    excerpt:
      'The first entry in our weekly changelog. Competitions got a memory, duels got live updates, freeze days got an undo button. Rest days stopped hurting your completion rate.',
    html: whatsNew20260726Html,
    category: 'changelog',
    hideHero: true,
  },
  {
    slug: 'notion-habit-competitions',
    category: 'guide',
    title: 'Habit Competitions in Notion: Compete With Friends on Real Streak Data',
    seoTitle: 'Habit Competitions in Notion: Groups, Duels & Leaderboards',
    description:
      'Compete with friends on habit data from your Notion database. How group competitions, leaderboards, and one-on-one duels work in Notion Habit Heroes.',
    datePublished: '2026-07-11',
    dateModified: '2026-11-01',
    image: 'assets/images/blog/og-blog-notion-habit-competitions.png',
    thumbnail: 'assets/images/blog/og-blog-notion-habit-competitions_800.webp',
    imageAlt:
      'Notion Habit Heroes blog card: Habit Competitions in Notion: Compete With Friends on Real Streak Data, above a podium bar chart with three initial avatars and their points.',
    excerpt:
      'Your Notion habit database already has the data. Here is how to put it on a leaderboard against a friend, and what happens when you challenge someone to a duel.',
    html: notionHabitCompetitionsHtml,
    faq: [
      {
        question: 'How do Notion habit competitions work?',
        answer: 'You create a competition, give it a name and a time window, and share the join link. Each person connects their own Notion habit database; Notion Habit Heroes reads checkboxes live on widget open, backed by a nightly safety-net sync, then updates a shared leaderboard. When the competition closes, a podium records three kinds of winners: most total points, best average completion percentage, and most perfect days. Everyone\'s competition total starts at zero and counts from the day before they join, regardless of their lifetime points.',
      },
      {
        question: 'What\'s the difference between a competition and a duel?',
        answer: 'A competition is for groups: everyone joins with their own Notion database, and the leaderboard runs for the window you set. Creating and joining a competition are both free. A duel is two people: share a link and whoever accepts first becomes your opponent, or send a direct email invite to one specific person. Starting a duel is free too. So is accepting one. Both formats sync live. A duel is decided by the higher average completion rate, with no podium at the end.',
      }
    ],
  },
  {
    slug: 'best-notion-habit-tracker-templates',
    category: 'guide',
    title: 'Best Notion Habit Tracker Templates: 12 Free Options for 2026',
    seoTitle: 'Best Notion Habit Tracker Templates: 12 Free Picks (2026)',
    description:
      '12 free Notion habit tracker templates for 2026, each loaded and verified actually free on October 21, 2026, with real strengths and weaknesses.',
    datePublished: '2026-07-29',
    dateModified: '2026-11-01',
    featured: true,
    image: 'assets/images/blog/og-blog-best-notion-habit-tracker-templates.png',
    thumbnail: 'assets/images/blog/og-blog-best-notion-habit-tracker-templates_800.webp',
    imageAlt:
      'Notion Habit Heroes blog card: Best Notion Habit Tracker Templates: 12 Free Options for 2026, beside a fanned stack of three mini template cards.',
    excerpt:
      'We loaded twelve Notion habit tracker templates and checked whether they are actually free, not just labeled that way. Here is what is inside each one, the honest weakness, and how they compare on streaks.',
    html: bestNotionHabitTrackerTemplatesHtml,
    faq: [
      {
        question: 'Are these Notion habit tracker templates really free?',
        answer: 'Nine are a genuine no-cost duplicate with no email required: eight are direct Marketplace duplicates, and ours duplicates from a plain Notion share link. Thomas Frank\'s Minimalist template asks for an email but lets you skip it. Two others, on Gumroad or the creator\'s own store, are listed at $0 but still require an email to check out. None of the twelve charge money. We loaded every page directly on October 21, 2026, and checked the live price; one template from the July edition is no longer for sale and was dropped.'
      },
      {
        question: 'Is Notion Habit Tracker free?',
        answer: 'Yes. Notion\'s own Habit Tracker template is free on the Marketplace, and Notion\'s Free plan runs every tracker on this list: unlimited pages and blocks for a personal workspace, databases and button blocks. Two limits matter: the Free plan allows one chart per workspace, and database automations that ship inside a template keep running but cannot be edited, while building your own needs a paid plan. Notion Habit Heroes is free too and counts streaks outside Notion, so it needs nothing beyond the Free plan.'
      },
      {
        question: 'Can Notion count habit streaks on its own?',
        answer: 'No. A Notion formula runs inside a single row and has no way to inspect a different row, so it cannot tell whether yesterday\'s entry was checked. That is a hard limitation of the database engine, not a missing setting. Templates that show a streak number get there one of two ways: heavy rollup and linked-database formulas that approximate a streak and tend to break when a day is skipped or edited out of order, or an outside integration that reads the checkboxes and does the counting itself. Notion Habit Heroes takes the second route: it reads your checkboxes live through the official Notion API every time you open the widget, with a nightly safety-net pass, then computes streaks from the actual history, rather than a formula guessing at it.',
      },
      {
        question: 'Which template is best for streaks?',
        answer: 'For a true current-and-longest streak counter built with formulas alone, Benny Builds It\'s Ultimate Notion Habit Tracker is the strongest of the eleven researched templates, with Joe\'s Habit Tracker with Analytics the runner-up. For a streak that is computed automatically instead of maintained by hand, and stays accurate even across schedule changes or a missed day, the Notion Habit Heroes template is the one built for that specifically, since the counting happens outside Notion rather than inside a formula column.',
      },
      {
        question: 'Which is best if I want to track habits with friends?',
        answer: 'None of the eleven independent templates support tracking habits with someone else on a shared leaderboard; Notion has no built-in way to combine data from two separate workspaces, so that has to come from outside Notion. The Notion Habit Heroes template pairs with a free Habit Heroes account that already handles this: accepting a duel invite from a friend is free. So is running your own competitions or leaderboards with friends.',
      },
      {
        question: 'Which is best for gamified habit tracking?',
        answer: 'For a game built inside Notion itself, Swift\'s Gamified Life Tracker is the pick: RPG attributes, gold, a shop, plus a dungeon tab, though the formulas are hand-maintained and check-ins stay manual. Kevechino\'s Gamified Habit Tracker is the better-known name and the easier start, but the free copy stops at earning EXP. For gamification driven by real data, the Notion Habit Heroes template is the pick: points per completion, duels, plus competition leaderboards, all computed from your actual checkbox history rather than a formula column you keep alive by hand.',
      }
    ],
  },
  {
    slug: 'notion-habit-tracker-mistakes',
    category: 'guide',
    title: 'Notion Habit Tracker Mistakes: Why Trackers Fail (and How to Fix Them)',
    seoTitle: 'Notion Habit Tracker Mistakes to Avoid in 2026',
    description:
      'The most common Notion habit tracker mistakes to avoid, and the fix for each one, from tracking too many habits to zero accountability.',
    datePublished: '2026-07-29',
    image: 'assets/images/blog/og-blog-notion-habit-tracker-mistakes.png',
    thumbnail: 'assets/images/blog/og-blog-notion-habit-tracker-mistakes_800.webp',
    imageAlt:
      'Notion Habit Heroes blog card: Notion Habit Tracker Mistakes: Why Trackers Fail (and How to Fix Them), beside a white card listing three crossed-out mistakes and one checked fix.',
    excerpt:
      'Most Notion habit trackers do not fail from lack of motivation. Here are the setup mistakes that quietly kill them, and the fix for each one.',
    html: notionHabitTrackerMistakesHtml,
    faq: [
      {
        question: 'Why do Notion habit trackers fail?',
        answer: 'Most Notion habit trackers fail from a handful of structural mistakes rather than a lack of willpower: too many habits added at once, a database over-engineered with fragile formulas before the habit even exists, or no feedback loop that makes the effort feel worth continuing. Because a Notion formula cannot inspect other rows in a database, it also cannot count a real streak across days, so plenty of trackers show a number that quietly stops meaning anything the first time an entry is skipped. Fixing the structure, not the person, is usually what gets a stalled tracker moving again.',
      },
      {
        question: 'What is the biggest habit tracking mistake?',
        answer: 'The single most common mistake is tracking too many habits at once. A database with a dozen or more checkbox columns turns what should be a two-minute daily ritual into a chore, and chores get skipped first and abandoned soon after. Keeping the list of checkbox habits under ten, and starting with far fewer than that, is the fastest way to keep a new tracker from being abandoned in its first few weeks.',
      },
      {
        question: 'Should I restart my streak after a missed day?',
        answer: 'Yes, and that is fine. A missed day should reset the streak count, not the habit itself or the tracker as a whole. Treating one missed day as proof the whole system failed is what causes most people to quit; treating it as a normal reset that costs you a number, not the underlying habit, is what keeps a tracker in use for months. Consistency across weeks matters more than the length of any single streak.',
      }
    ],
  },
  {
    slug: 'notion-habit-tracker-google-calendar',
    category: 'guide',
    title: 'How to Sync Your Notion Habit Tracker With Google Calendar',
    seoTitle: 'Sync Your Notion Habit Tracker With Google Calendar',
    description:
      'Sync your Notion habit tracker with Google Calendar so every habit gets a real time slot. Automatic sync setup in three steps, with streaks and points on top.',
    datePublished: '2026-07-28',
    dateModified: '2026-07-29',
    image: 'assets/images/blog/nh2-2sync.webp',
    imageAlt: 'Notion Habit Heroes and 2sync logos connected by sync arrows',
    excerpt:
      'Your Notion tracker knows which habits you should do; your calendar decides when they happen. How to connect the two with an automatic sync, and let streaks keep score.',
    html: notionHabitTrackerGoogleCalendarHtml,
  },
  {
    slug: 'gamified-notion-habit-tracker',
    category: 'guide',
    title: 'A Gamified Notion Habit Tracker That Runs on Real Data',
    seoTitle: 'Gamified Notion Habit Tracker: Points & Leaderboards',
    description:
      'How a gamified Notion habit tracker works: streaks, points, leaderboards, and duels computed from your real checkboxes, with the RPG theme in the artwork.',
    datePublished: '2026-07-14',
    dateModified: '2026-07-29',
    image: 'assets/images/blog/og-blog-gamified-notion-habit-tracker.png',
    thumbnail: 'assets/images/blog/og-blog-gamified-notion-habit-tracker_800.webp',
    imageAlt:
      'Notion Habit Heroes blog card: A Gamified Notion Habit Tracker That Runs on Real Data, beside a flame icon over a 42-day streak counter.',
    excerpt:
      'Points, streaks, and a leaderboard, all computed from checkboxes you actually ticked. Here is how Notion Habit Heroes turns a Notion database into a game worth taking seriously.',
    html: gamifiedNotionHabitTrackerHtml,
  },
  {
    slug: 'story-behind-notion-habit-heroes',
    category: 'essay',
    title: 'Building a Habit Tracker Inside Notion: The Story Behind NH2',
    seoTitle: 'Building a Habit Tracker in Notion: 3.5 Years, 475 Commits',
    description:
      'One solo developer, 3.5 years, 475 commits: the honest story of building a habit tracker inside Notion. The pivot, the rewrites, the silent failures.',
    datePublished: '2026-07-05',
    dateModified: '2026-07-16',
    image: 'assets/images/blog/og-blog-story-behind-notion-habit-heroes.png',
    thumbnail: 'assets/images/blog/og-blog-story-behind-notion-habit-heroes_800.webp',
    imageAlt:
      'Notion Habit Heroes blog card: Building a Habit Tracker Inside Notion: The Story Behind NH2, above a four-point timeline from first commit to today.',
    excerpt:
      "475 commits, three payment-system attempts, and one outage I didn't notice for three months. The unfiltered story of building Notion Habit Heroes solo.",
    html: storyBehindNotionHabitHeroesHtml,
  },
  {
    slug: 'best-habit-hero-apps',
    category: 'guide',
    title: 'Habit Hero: Which of the Six Apps Do You Mean?',
    seoTitle: 'Habit Hero: Which of the Six Apps Do You Mean? (2026)',
    description:
      'Searching for Habit Hero? Six apps share the name: fitness, iPhone, kids routines, web, a Notion template and Notion Habit Heroes. Find the one you mean in a minute.',
    datePublished: '2026-07-06',
    dateModified: '2026-09-18',
    image: 'assets/images/blog/og-blog-best-habit-hero-apps-v2.png',
    thumbnail: 'assets/images/blog/og-blog-best-habit-hero-apps-v2_800.webp',
    imageAlt:
      'Notion Habit Heroes blog card: Habit Hero: Which of the Six Apps Do You Mean?, above a row of five app tiles each with a star rating.',
    excerpt:
      'Half a dozen apps share the name "Habit Hero" and they have almost nothing in common: a fitness-competition app, a kids routine builder, an iPhone tracker, an auto-completing web app, a Notion template, and us. Here is which one is actually for you.',
    html: bestHabitHeroAppsHtml,
  },
  {
    slug: 'habit-hero-template-vs-notion-habit-heroes',
    category: 'guide',
    title: 'Habit Hero Notion Template vs. Notion Habit Heroes: Compared',
    seoTitle: 'Habit Hero Notion Template vs. Notion Habit Heroes Compared',
    description:
      'One is a free static Notion template, the other syncs your checkboxes into live streaks, points and leaderboards. An honest comparison of two similar names.',
    datePublished: '2026-07-06',
    dateModified: '2026-07-29',
    image: 'assets/images/blog/og-blog-habit-hero-template-vs-notion-habit-heroes.png',
    thumbnail: 'assets/images/blog/og-blog-habit-hero-template-vs-notion-habit-heroes_800.webp',
    imageAlt:
      'Notion Habit Heroes blog card: Habit Hero Notion Template vs. Notion Habit Heroes: Compared, above a two-tone split with a VS badge between a static checklist and a streak and points chip.',
    excerpt:
      "People mix these two up constantly, and fair enough. One is a free static template, the other a synced game layer on top of your database. An honest comparison.",
    html: habitHeroTemplateVsNotionHabitHeroesHtml,
  },
  {
    slug: 'social-habit-tracking',
    category: 'guide',
    title: 'Notion Habit Tracker with Friends: 3 Ways to Track Habits Together',
    seoTitle: 'Notion Habit Tracker with Friends: 3 Ways to Share Habits',
    description:
      'Want a Notion habit tracker to share with friends, a partner or a group? Compare a shared database, two-person templates and Notion Habit Heroes.',
    datePublished: '2026-07-05',
    dateModified: '2026-11-01',
    image: 'assets/images/blog/og-blog-social-habit-tracking.png',
    thumbnail: 'assets/images/blog/og-blog-social-habit-tracking_800.webp',
    imageAlt:
      'Notion Habit Heroes blog card: Social Habit Tracking: Why Doing It With Friends Works Better, beside four overlapping avatars and a speech bubble reading Nice streak!',
    excerpt:
      'Three ways to share habit tracking in Notion with a friend, a partner or a group: what each one handles well, where it stops and which one to pick.',
    html: socialHabitTrackingHtml,
    faq: [
      {
        question: 'What habit tracker can I share with friends?',
        answer: 'In Notion you can share one habit database with a Person property, use a two-person template, or connect each person\'s own database to Notion Habit Heroes. The first two put everyone on one shared page. Notion Habit Heroes lets everyone keep their own tracker while competing on a shared leaderboard or in 1-on-1 duels, for free.',
      },
      {
        question: 'Can two people use the same Notion habit tracker?',
        answer: 'Yes. Share the page, add a Person property and give each person a filtered view, or start from a template built for two people. Couples often go this way. If you\'d rather keep separate trackers, each of you can connect your own database to Notion Habit Heroes and start a duel.',
      },
      {
        question: 'Do my friends need to be in my Notion workspace?',
        answer: 'Not with Notion Habit Heroes. Each person connects the habit database in their own workspace. The competition or duel brings everyone together. A shared database or a shared template only works if everyone can open the same page.',
      },
      {
        question: 'Can I keep some habits private from my friends?',
        answer: 'Yes. In Edit Habits you can mark any habit as private. Other users won\'t see it. Private habits also can\'t be picked for a duel. Your other habits stay visible as usual.',
      },
      {
        question: 'Is tracking habits with friends free?',
        answer: 'Yes. Sharing a page costs nothing beyond your Notion plan. Notion Habit Heroes is free for individuals with no time limit. Competitions, duels, streaks and the widget are all included, with no card required.',
      },
    ],
  },
  {
    slug: 'gamify-your-habits',
    category: 'guide',
    title: 'How to Gamify Your Habits (and Actually Stick With Them)',
    seoTitle: 'Habit Gamification: How to Turn Routines Into a Game',
    description:
      'A practical guide to habit gamification: use points, streaks, and leaderboards so consistency feels like progress instead of a chore.',
    datePublished: '2026-07-05',
    dateModified: '2026-07-29',
    image: 'assets/images/blog/og-blog-gamify-your-habits.png',
    thumbnail: 'assets/images/blog/og-blog-gamify-your-habits_800.webp',
    imageAlt:
      'Notion Habit Heroes blog card: How to Gamify Your Habits (and Actually Stick With Them), above a level 7 progress bar and three reward chips.',
    excerpt:
      "Willpower is a bad long-term strategy. Here's how to borrow the mechanics that make games addictive and point them at your habits instead.",
    html: gamifyYourHabitsHtml,
  },
  {
    slug: 'notion-habit-tracker',
    category: 'guide',
    title: 'How to Build a Notion Habit Tracker That Actually Keeps You Motivated',
    seoTitle: 'Notion Habit Tracker: Setup Guide with Free Template (2026)',
    description:
      'Set up a habit tracker in Notion and supercharge it with automatic streaks, points and friendly competitions. Step-by-step guide with a free template.',
    datePublished: '2026-07-05',
    dateModified: '2026-10-27',
    image: 'assets/images/blog/og-blog-notion-habit-tracker.png',
    thumbnail: 'assets/images/blog/og-blog-notion-habit-tracker_800.webp',
    imageAlt:
      'Notion Habit Heroes blog card: How to Build a Notion Habit Tracker That Actually Keeps You Motivated, beside a mock journal table with checked and unchecked habit rows.',
    excerpt:
      'A plain checkbox database records what you did, but it cannot motivate you on its own. Here\'s how to turn your Notion habit tracker into a system that keeps you going.',
    html: notionHabitTrackerHtml,
    faq: [
      {
        question: 'Can Notion track habits on its own?',
        answer: 'Notion can store habit data and display daily checkbox entries in a database, but it cannot count streaks, award points, or hold you accountable on its own. Streak calculation requires reading across multiple rows in date order, which the Notion database engine does not support natively. A separate sync layer is needed to turn checkboxes into streak and point data. What Notion does well is give you a database you already own, with full control over which habits you track and how the page is laid out. Notion Habit Heroes connects to that database via official Notion OAuth and adds the tracking layer without requiring you to migrate your data to a new system or adopt a different note-taking tool.',
      },
      {
        question: 'Can Notion formulas count streaks?',
        answer: 'Notion formulas cannot count consecutive streaks across separate daily-row entries in a database. A formula runs within a single row and cannot inspect other rows to determine whether the previous day was checked. Any streak counter visible in a Notion template is either a rough approximation built from rollups or breaks when a day\'s entry is missing. Notion Habit Heroes runs streak calculations every time you open your widget, against every synced entry in date order, with the nightly pass as the safety net for days you never opened it. The numbers are always current and do not require template maintenance.',
      },
      {
        question: 'How long does it take to build a habit?',
        answer: 'Research by Lally et al. (2010) found a median of 66 days for a new behavior to reach automaticity, with results ranging from 18 to 254 days depending on the person and the habit. Missing a single day did not materially set back the formation process. The common "21 days" figure is not supported by behavioral research. Give any new habit at least two months before deciding it is not working, and treat one missed day as noise rather than failure. That is what freeze days in Notion Habit Heroes are designed to protect against.',
      },
      {
        question: 'What should a Notion habit tracker database include?',
        answer: 'A Notion habit database needs at minimum one date property per row to anchor each entry to a calendar day, and one checkbox property per habit you want to track. Formulas, rollups, and additional metadata are optional. Beyond the minimum, useful additions include a text property for a one-line daily note, a select for mood or energy level, and filtered views for the current week. Keep the list of checkbox habits under ten; more than that and the daily entry becomes laborious rather than a quick ritual.',
      },
      {
        question: 'Should you track habits daily or weekly?',
        answer: 'In the widget\'s Edit Habits view, each habit gets a schedule: daily, every N days, or N times per week. Streaks are schedule-aware, so a habit scheduled every seven days does not break its streak on the days in between. The streak only counts against you when a habit was due and you missed it: the interval deadline passes without a check, or the week closes under quota. The daily completion percentage also counts only habits due that day. Give each habit its honest cadence and the streak math stays accurate.',
      },
      {
        question: 'Is a Notion habit tracker better than a standalone habit app?',
        answer: 'A Notion habit tracker suits people who already use Notion daily as their main productivity tool: habits live in the same place as notes and tasks, with no context switching between apps. A standalone habit app is simpler to start and delivers phone notifications natively. The better choice depends on where you already spend your daily planning time. If you are already in Notion every day for work, notes, or journaling, adding habit checkboxes to your existing workspace and connecting the app adds motivation without introducing a new tool.',
      }
    ],
  },
];
